import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FallbackError } from '@bloomays-lib/ui.shared';
import { styled } from '@mui/material/styles';
import { useQuery } from '@apollo/client';
import {
  ContractsFilterInput,
  ContractVoletFilter,
  DocumentStatusFilter,
  GetLegalContractsGQLParams,
  GetLegalContractsWithSummaryGQLResult,
  LineTypeFilter,
  MissionStatusFilter,
} from '@bloomays-lib/types.shared';
import type { ContractSegment } from '@bloomays-lib/utils.shared';
import { GET_LEGAL_CONTRACTS_WITH_SUMMARY } from '@bloomays-lib/adapter.api-bloomer';
import { LoaderPending } from '@bloomays-lib/ui.shared';
import TableLegalContracts, {
  type ServerContractFilters,
  type SigningStateQuickFilter,
} from '../organisms/TableLegalContracts';
import { PageHeader } from '../molecules/PageHeader';
import ContractsSummaryBanner from '../molecules/ContractsSummaryBanner';
import ContractsKpiCards from '../organisms/ContractsKpiCards';

const NO_SERVER_FILTERS: ServerContractFilters = {
  signingState: '',
  contractType: '',
  accountManagerEmail: '',
  search: '',
  volet: '',
};

const toContractsFilterInput = (filters: ServerContractFilters): ContractsFilterInput | undefined => {
  const documentStatus =
    filters.signingState === 'toSign'
      ? [DocumentStatusFilter.toSign]
      : filters.signingState === 'signed'
      ? [DocumentStatusFilter.signed]
      : undefined;
  const lineType =
    filters.contractType === 'main'
      ? LineTypeFilter.principal
      : filters.contractType === 'amendment'
      ? LineTypeFilter.avenant
      : undefined;
  const accountManagerEmail = filters.accountManagerEmail ? [filters.accountManagerEmail] : undefined;
  const volet =
    filters.volet === 'SST'
      ? [ContractVoletFilter.SST]
      : filters.volet === 'PDS'
      ? [ContractVoletFilter.PDS]
      : undefined;
  if (!documentStatus && !lineType && !accountManagerEmail && !volet) return undefined;
  return { documentStatus, lineType, accountManagerEmail, volet };
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 20;

const Contracts = ({ play = true }: { play?: boolean }) => {
  const { t } = useTranslation(['legalContracts', 'mission']);
  const [status, setStatus] = useState<MissionStatusFilter>(MissionStatusFilter.ACTIVE);
  const [signingStateFilter, setSigningStateFilter] = useState<SigningStateQuickFilter>('');
  const [activeSegment, setActiveSegment] = useState<ContractSegment | ''>('');
  const [serverFilters, setServerFilters] = useState<ServerContractFilters>(NO_SERVER_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const handleSegmentCardClick = useCallback((segment: ContractSegment) => {
    setActiveSegment((current) => (current === segment ? '' : segment));
  }, []);

  const filter = useMemo(() => toContractsFilterInput(serverFilters), [serverFilters]);
  const search = serverFilters.search || undefined;

  // Must be memoized: TableLegalContracts depends on this in a useEffect — a new ref each render loops.
  const handleServerFiltersChange = useCallback((filters: ServerContractFilters) => {
    setServerFilters(filters);
    setPage(0);
  }, []);
  const handleStatusChange = (next: MissionStatusFilter) => {
    setStatus(next);
    setPage(0);
  };
  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setPage(0);
  };

  const pagination = useMemo(() => ({ limit: pageSize, offset: page * pageSize }), [page, pageSize]);

  const {
    loading,
    error,
    data: resultData,
    previousData,
    refetch,
  } = useQuery<GetLegalContractsWithSummaryGQLResult, GetLegalContractsGQLParams>(GET_LEGAL_CONTRACTS_WITH_SUMMARY, {
    variables: { status, filter, search, pagination },
  });

  // Keep previous data while refetching so the table stays mounted (preserves search / filter UI state).
  const data = resultData?.getLegalContractsWithSummary ?? previousData?.getLegalContractsWithSummary;
  const missions = data?.missions ?? [];
  const pageInfo = data?.pageInfo;
  const accountManagerOptions = useMemo(
    () => (data?.availableAccountManagers ?? []).map((am) => ({ value: am.email, label: am.name })),
    [data?.availableAccountManagers],
  );

  const totalMissions = pageInfo?.total ?? 0;
  const rangeStart = totalMissions === 0 ? 0 : (pageInfo?.offset ?? 0) + 1;
  const rangeEnd = (pageInfo?.offset ?? 0) + missions.length;
  const canPrev = page > 0;
  const canNext = pageInfo?.hasNextPage ?? false;

  return (
    <Container>
      <PageHeader
        id="titleAdminDashboardAllContracts"
        title={t('pageTitle')}
        onRefresh={async () => {
          await refetch();
        }}
      >
        <FilterGroupRoot>
          <FilterLabel>{t('statusLabel', { ns: 'mission' })}</FilterLabel>
          <StatusSelect
            value={status}
            aria-label={t('statusLabel', { ns: 'mission' })}
            onChange={(e) => handleStatusChange(e.target.value as MissionStatusFilter)}
          >
            <option value={MissionStatusFilter.ACTIVE}>{t('active', { ns: 'mission' })}</option>
            <option value={MissionStatusFilter.INCOMING}>{t('incoming', { ns: 'mission' })}</option>
            <option value={MissionStatusFilter.FINISHED}>{t('finished', { ns: 'mission' })}</option>
          </StatusSelect>
        </FilterGroupRoot>
      </PageHeader>
      {!error && data ? (
        <KpiCardsSlot>
          <ContractsKpiCards segments={data.summary.segments} onSegmentChange={handleSegmentCardClick} />
        </KpiCardsSlot>
      ) : null}
      {!error && data ? (
        <BannerSlot>
          <ContractsSummaryBanner summary={data.summary} onSeeAll={() => setSigningStateFilter('toSign')} />
        </BannerSlot>
      ) : null}
      <TableContainer>
        {error ? (
          <FallbackError errorText={t('errorFetchingContracts')}></FallbackError>
        ) : !data ? (
          <LoaderPending text={t('fecthingAllContracts')} rotate={play} />
        ) : (
          <TableLegalContracts
            missionsContracts={missions}
            loading={loading}
            serverPaginated
            signingStateFilter={signingStateFilter}
            onSigningStateFilterChange={setSigningStateFilter}
            segmentFilter={activeSegment}
            onSegmentFilterChange={setActiveSegment}
            accountManagerOptions={accountManagerOptions}
            onServerFiltersChange={handleServerFiltersChange}
            noRowsLabel={t('noMission', { ns: 'mission' })}
          />
        )}
      </TableContainer>
      {!error && data ? (
        <PaginationBar>
          <PageSizeGroup>
            <span>{t('missionsPerPage')}</span>
            <PageSizeSelect
              value={pageSize}
              aria-label={t('missionsPerPage')}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PageSizeSelect>
          </PageSizeGroup>
          <RangeLabel>{t('paginationRange', { start: rangeStart, end: rangeEnd, total: totalMissions })}</RangeLabel>
          <PagerButtons>
            <PagerButton
              type="button"
              aria-label={t('previousPage')}
              disabled={!canPrev || loading}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              ‹
            </PagerButton>
            <PagerButton
              type="button"
              aria-label={t('nextPage')}
              disabled={!canNext || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              ›
            </PagerButton>
          </PagerButtons>
        </PaginationBar>
      ) : null}
    </Container>
  );
};

export default Contracts;

const Container = styled('div')(() => ({
  margin: 'auto',
  width: '90%',
}));

const KpiCardsSlot = styled('div')(() => ({
  marginTop: 16,
}));

const BannerSlot = styled('div')(() => ({
  marginTop: 16,
}));

const TableContainer = styled('div')(() => ({
  marginTop: 16,
}));

const PaginationBar = styled('div')(({ theme }) => ({
  marginTop: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 20,
  fontSize: 13,
  color: theme.palette.neutral[80],
}));

const PageSizeGroup = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const PageSizeSelect = styled('select')(({ theme }) => ({
  height: 30,
  borderRadius: 8,
  border: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral.white,
  padding: '0 8px',
  fontSize: 13,
  cursor: 'pointer',
}));

const RangeLabel = styled('span')(() => ({
  fontVariantNumeric: 'tabular-nums',
}));

const PagerButtons = styled('div')(() => ({
  display: 'flex',
  gap: 4,
}));

const PagerButton = styled('button')(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral.white,
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
  color: theme.palette.neutral[80],
  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
}));

const FilterGroupRoot = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 6,
  width: 'fit-content',
}));

const FilterLabel = styled('span')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const StatusSelect = styled('select')(({ theme }) => ({
  height: 38,
  width: 220,
  padding: '0 32px 0 12px',
  borderRadius: 10,
  border: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral.white,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239898A8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
}));
